import React, { useState, useEffect, useRef } from "react";
import * as Mui from "@mui/material";
import { themes } from "services/constants";
import { InfoMessage } from "components/CustomComponents/CustomButton";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { Loader } from "components/Loader";
import sessionHandling from "services/utils/notificationUtils";
import { Description } from "@mui/icons-material";
import PayslipData from "./PayslipData";
import { BlobProvider } from "@react-pdf/renderer";
import { DoubleArrow, Download } from "@mui/icons-material";

const PayslipList = () => {
  const { domain, token, userDetails, globalSearchValue } = useSelector(
    (state) => state.tokenReducer
  );
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [payslipsList, setPayslipsList] = useState([]);
  const [pslipOpenDialog, setPslipOpenDialog] = useState(false);
  const [indPayslipData, setIndPayslipData] = useState(null);
  const [masterDataForSearch, setMasterDataForSearch] = useState([]);
  const [pdfKey, setPdfKey] = useState(0);
  const iframeRef = useRef(null);

  useEffect(() => {
    const fetchUserPayslipDetails = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(
          `${domain}payroll/operations/payrollprocessing/payrollruncycle/user-payslip-details/?user_id=${userDetails?.id}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          }
        );
        const res = await response.json();
        if (response.ok) {
          setPayslipsList(res);
          setMasterDataForSearch(res);
        } else if (response.status === 409) {
          setPayslipsList([]);
          setMasterDataForSearch([]);
          sessionHandling();
        } else {
          setPayslipsList([]);
          setMasterDataForSearch([]);
          throw new Error(res.error);
        }
      } catch (error) {
        setPayslipsList([]);
        setMasterDataForSearch([]);
        showToast("error", error.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserPayslipDetails();
  }, [domain, token, userDetails]);

  useEffect(() => {
    const filteredResults = masterDataForSearch?.filter((item) =>
      ["pay_schedule", "start_date", "end_date", "pay_date", "net_pay"].some(
        (key) =>
          item[key]
            ?.toString()
            .toLowerCase()
            .includes(globalSearchValue.toString().toLowerCase())
      )
    );
    setPayslipsList(filteredResults);
    setPage(0);
  }, [globalSearchValue, masterDataForSearch]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getPayslipData = async (payschle) => {
    try {
      const response = await fetch(
        `${domain}payroll/operations/payrollprocessing/payrollruncycle/get-payslip-details/?user_id=${payschle?.user_id}&pay_schedule_id=${payschle?.pay_schedule_id}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
        }
      );
      const jsonData = await response.json();
      if (response.ok) {
        setIndPayslipData(jsonData);
        setPslipOpenDialog(true);
        setPdfKey((prev) => prev + 1);
      } else if (response.status === 409) {
        sessionHandling();
      } else {
        throw new Error(jsonData?.error);
      }
    } catch (error) {
      showToast("error", error.message || "Something went wrong");
      setIndPayslipData(null);
      setPslipOpenDialog(false);
    }
  };

  const handlePress = async (payschle) => {
    try {
      await getPayslipData(payschle);
    } catch (error) {
      showToast("error", error.message);
    }
  };

  const toggleDrawer = () => {
    setPslipOpenDialog(false);
    setIndPayslipData(null);
    if (iframeRef.current) {
      iframeRef.current.src = "";
    }
  };

  return (
    <React.Fragment>
      <Mui.Grid container spacing={1} sx={{ mt: 0 }}>
        <Mui.Grid item xs={12} sm={12} md={12} lg={12}>
          <Mui.Stack direction="row">
            <Mui.Typography
              sx={{
                fontSize: 20,
                fontWeight: "bold",
                mt: 1.5,
              }}
            >
              My Payslip
            </Mui.Typography>
          </Mui.Stack>
        </Mui.Grid>
      </Mui.Grid>
      <Mui.Grid container spacing={1} sx={{ mt: 1 }}></Mui.Grid>

      {isLoading ? (
        <Loader info="Loading..." />
      ) : (
        <>
          <Mui.Grid
            container
            sx={{
              paddingRight: 1,
            }}
          >
            <Mui.TableContainer
              component={Mui.Paper}
              style={{ paddingTop: 4, paddingBottom: 2 }}
              sx={{
                mt: 2,
                borderBottom: "1.5px solid transparent",
              }}
            >
              <Mui.Table>
                <Mui.TableHead>
                  <Mui.TableRow
                    maxWidth="xl"
                    align="left"
                    sx={{ backgroundColor: themes.primary }}
                  >
                    <Mui.TableCell
                      sx={{
                        color: themes.blackColor,
                        fontWeight: "bold",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: 100,
                      }}
                    >
                      Pay Period
                    </Mui.TableCell>
                    <Mui.TableCell
                      sx={{
                        color: themes.blackColor,
                        fontWeight: "bold",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: 100,
                      }}
                    >
                      Payslip Name
                    </Mui.TableCell>
                    <Mui.TableCell
                      sx={{
                        color: themes.blackColor,
                        fontWeight: "bold",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: 100,
                      }}
                    >
                      Pay Date
                    </Mui.TableCell>
                    <Mui.TableCell
                      sx={{
                        color: themes.blackColor,
                        fontWeight: "bold",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: 100,
                      }}
                    >
                      Net Pay
                    </Mui.TableCell>
                    <Mui.TableCell
                      sx={{
                        color: themes.blackColor,
                        fontWeight: "bold",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: 100,
                      }}
                    >
                      Action
                    </Mui.TableCell>
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {payslipsList?.length === 0 ? (
                    <InfoMessage dataLength={masterDataForSearch} />
                  ) : (
                    payslipsList
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((item, index) => (
                        <Mui.TableRow
                          key={index}
                          maxWidth="xl"
                          align="left"
                          sx={{
                            "& .MuiTableCell-root": {
                              height: "15px",
                              padding: "2px 15px",
                            },
                          }}
                          hover={true}
                        >
                          <Mui.TableCell
                            sx={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              maxWidth: 100,
                            }}
                          >
                            <Mui.Typography
                              sx={{
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {item?.start_date}
                              {" to "}
                              {item?.end_date}
                            </Mui.Typography>
                          </Mui.TableCell>
                          <Mui.TableCell
                            sx={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              maxWidth: 100,
                            }}
                          >
                            <Mui.Typography
                              sx={{
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {item?.pay_schedule_name}
                            </Mui.Typography>
                          </Mui.TableCell>
                          <Mui.TableCell
                            sx={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              maxWidth: 100,
                            }}
                          >
                            <Mui.Typography
                              sx={{
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {item?.pay_date}
                            </Mui.Typography>
                          </Mui.TableCell>
                          <Mui.TableCell
                            sx={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              maxWidth: 100,
                            }}
                          >
                            <Mui.Typography
                              sx={{
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {item?.net_pay}
                            </Mui.Typography>
                          </Mui.TableCell>
                          <Mui.TableCell
                            sx={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              maxWidth: 100,
                            }}
                          >
                            <Mui.IconButton
                              onClick={() => {
                                handlePress(item);
                              }}
                            >
                              <Description
                                sx={{
                                  cursor: "pointer",
                                  color: themes.redColor,
                                  strokeWidth: 2,
                                }}
                              />
                            </Mui.IconButton>
                          </Mui.TableCell>
                        </Mui.TableRow>
                      ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
              {payslipsList.length > 10 ? (
                <Mui.TablePagination
                  className="custom-pagination"
                  component="div"
                  rowsPerPageOptions={[10, 20, 50, 100]}
                  count={payslipsList.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              ) : null}
            </Mui.TableContainer>
          </Mui.Grid>
        </>
      )}

      {pslipOpenDialog && indPayslipData && (
        <Mui.Drawer
          anchor="right"
          open={pslipOpenDialog}
          onClose={(event, reason) => {
            if (reason === "backdropClick" || reason === "escapeKeyDown") {
              return;
            }
            toggleDrawer();
          }}
          sx={{
            "& .MuiDrawer-paper": {
              width: "680px",
              backgroundColor: themes?.whiteColor,
            },
          }}
        >
          <Mui.Box
            sx={{
              height: "100vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Mui.Paper
              elevation={0}
              sx={{
                borderBottom: `1px solid ${themes?.miliedLightGray}`,
                borderRadius: 0,
              }}
            >
              <Mui.Box sx={{ padding: 1, backgroundColor: themes?.primary }}>
                <Mui.Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 1,
                  }}
                >
                  <Mui.Box sx={{ ml: 1 }}>
                    <Mui.IconButton
                      onClick={toggleDrawer}
                      sx={{
                        color: themes?.grayColor,
                        "&:hover": {
                          backgroundColor: themes?.whiteColor,
                        },
                      }}
                    >
                      <DoubleArrow />
                    </Mui.IconButton>
                  </Mui.Box>
                  <BlobProvider
                    key={pdfKey}
                    document={<PayslipData payslipData={indPayslipData} />}
                  >
                    {({ blob, url, loading }) => (
                      <>
                        <Mui.IconButton
                          disabled={loading || !url}
                          onClick={() => {
                            if (url) {
                              const link = document.createElement("a");
                              link.href = url;
                              link.download = `${
                                indPayslipData?.user_name +
                                  indPayslipData?.pay_schedule_name || "payslip"
                              }.pdf`;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }
                          }}
                          sx={{
                            color: themes?.greenColor,
                            backgroundColor: themes?.primary,
                            borderRadius: "50%",
                            "&:hover": { backgroundColor: themes?.primary },
                            mr: 2,
                            mt: 0.5,
                          }}
                        >
                          <Download />
                        </Mui.IconButton>
                      </>
                    )}
                  </BlobProvider>
                </Mui.Box>

                <Mui.Box sx={{ ml: 2.2 }}>
                  <Mui.Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {indPayslipData?.user_name +
                      indPayslipData?.pay_schedule_name || "payslip"}
                  </Mui.Typography>
                </Mui.Box>
              </Mui.Box>
            </Mui.Paper>

            <Mui.Box sx={{ flex: 1, overflow: "auto" }}>
              <BlobProvider
                key={pdfKey}
                document={<PayslipData payslipData={indPayslipData} />}
              >
                {({ blob, url, loading }) => {
                  if (loading) {
                    return <Loader info="Generating PDF..." />;
                  }
                  if (url) {
                    return (
                      <iframe
                        ref={iframeRef}
                        src={url}
                        style={{
                          width: "100%",
                          height: "100%",
                          minHeight: "calc(100vh - 64px)",
                          border: "none",
                        }}
                        title="Payslip Preview"
                      />
                    );
                  }
                  return null;
                }}
              </BlobProvider>
            </Mui.Box>
          </Mui.Box>
        </Mui.Drawer>
      )}
    </React.Fragment>
  );
};

export default PayslipList;
